/**
 * useWeeklyCellModel.ts
 * Headless model for ONE Task×Week cell in the Phase-2 employee-planning grid.
 *
 * This hook owns ALL behavior previously inlined in Phase2WeeklyCell: deriving the
 * machine-booking groups that overlap the week, the removing / open-picker state,
 * and the remove/assign handlers. Two presentations consume it unchanged — the
 * Classic card (Phase2WeeklyCell) and the compact Board card (Phase2BoardCell) —
 * so the two views can never drift in behavior; only pixels differ.
 *
 * Wave 3 (percent retirement, R2): the 0–100 slider machinery is GONE —
 * persistPercent / changePercent / optimisticPercents / the debounced PUT and
 * the per-group percent sum were all deleted. An assignment is PRESENCE (a
 * busy day); the server pins Percent=100 on every write. Remove, picker,
 * save-error and the W2-8 stranding check are unchanged.
 *
 * Pure helpers (bookingSegmentInWeek / belongsToBooking / dedupeAnchorKeys) live here
 * too and are exported for the presentational components.
 */

import { useCallback, useMemo, useState } from 'react';
import { deleteEmployeeAssignment } from '../../../../services/scheduleAssignments';
import { type ConfirmDialogState, CONFIRM_INITIAL } from '../../../../components/shared/ConfirmDialog';
import { weekEndFriday, weekOverlapsRange, fmtShortDate } from '../../../../utils/weekUtils';
import {
  isContinuousRun,
  continuousAttendanceWindows,
} from '../../../../utils/runModeRendering';
import {
  checkRemovalStranding,
  type RemovalStrandResult,
  type TaskWithBookings,
  type TaskAssignee,
} from '../../../../services/scheduleTasks';

// ---------------------------------------------------------------------------
// Pure helpers (no hooks) — exported for the cell presentations
// ---------------------------------------------------------------------------

/**
 * Compute the portion of a booking that falls within a week.
 * Returns { effectiveStart, effectiveEnd, days } or null if no overlap.
 */
export function bookingSegmentInWeek(
  bookingStart: string,
  bookingEnd: string,
  weekStart: string,
): { effectiveStart: string; effectiveEnd: string; days: number } | null {
  const weekEnd = weekEndFriday(weekStart);
  if (!weekOverlapsRange(weekStart, bookingStart, bookingEnd)) return null;
  const effectiveStart = bookingStart > weekStart ? bookingStart : weekStart;
  const effectiveEnd = bookingEnd < weekEnd ? bookingEnd : weekEnd;
  const d1 = new Date(`${effectiveStart}T00:00:00Z`).getTime();
  const d2 = new Date(`${effectiveEnd}T00:00:00Z`).getTime();
  const days = Math.round((d2 - d1) / 86_400_000) + 1;
  return { effectiveStart, effectiveEnd, days: days > 0 ? days : 1 };
}

/** Dedupe rows to ONE anchor EmployeeAssignmentKey per user (for booking-resize span). */
export function dedupeAnchorKeys(rows: TaskAssignee[]): string[] {
  return [...new Map(rows.map((a) => [a.UserKey, a.EmployeeAssignmentKey])).values()];
}

/** Whether an assignment row belongs to a given booking (null = task-level). */
export function belongsToBooking(a: TaskAssignee, bookingKey: string): boolean {
  return (a.MachineBookingKey ?? null) === bookingKey;
}

/** Sentinel picker id for the task-level (no specific machine) "+" group. */
export const TASK_LEVEL_PICKER = '__task__';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A machine booking's portion that overlaps the cell's week. */
export interface MachineSegment {
  key: string;
  code: string;
  name: string;
  bookingStart: string;
  bookingEnd: string;
  status: 'Tentative' | 'Committed';
  effectiveStart: string;
  effectiveEnd: string;
  days: number;
}

/** One machine group within the cell — its segment and its assignees. */
export interface WeeklyCellMachineGroup {
  seg: MachineSegment;
  /** Rows for THIS week assigned to this machine (or folded-in task-level rows). */
  weekAssignees: TaskAssignee[];
  /** One anchor key per user across all weeks (for booking-resize span). */
  anchorKeys: string[];
  /** Nobody is assigned to this machine this week — drives the "add someone" cue. We no
   *  longer compare against an auto-scaled % target (it doesn't translate across multi-day
   *  / multi-week tests, Gigi 2026-06-25); any assignee at all = staffed. Lab answer Q2
   *  (2026-07-10): continuous staffs the FULL duration, so mid-run weeks of a continuous
   *  booking are judged like any other week (the RM-6 mid-run exemption is retired). */
  under: boolean;
  /**
   * This week of a CONTINUOUS (24/7) booking contains NO staffable working day
   * (the full-duration windows — lab answer Q2, 2026-07-10 — cover every week
   * of [setup..teardown], so this only remains true for the rare all-holiday
   * lead-in week where the setup anchors into a later week). Staffing such a
   * week would be rejected by the backend (400 "outside this booking's staffed
   * weeks"), so presentations show a calm caption and hide the Add CTA.
   * Ordinary mid-run weeks are now STAFFED weeks — normal cues apply (RM-6's
   * "running unattended" model is retired).
   */
  midRunContinuous: boolean;
}

export interface UseWeeklyCellModelArgs {
  task: TaskWithBookings;
  /** ISO Saturday 'YYYY-MM-DD' for this column. */
  weekStart: string;
  /** All assignments for this task (all weeks, all users). Filtered here by weekStart. */
  assignments: TaskAssignee[];
  /** Reload the shared assignment map for this task after add/remove. */
  onAssignmentsChanged: (taskKey: string) => void;
  /** Re-fetch the capacity table after any staffing change. */
  onAllocationChanged?: () => void;
  /** 6C.6 baseline (no-scenario) rows for this task, all weeks. */
  baselineAssignments?: Array<{ UserKey: string; WeekStart: string; Percent: number }>;
  /** When true, expose the baseline-presence ghost markers. */
  compareToBaseline?: boolean;
  /**
   * W2-8 (P-15): active scenario key — scopes the pre-removal strand check to
   * the same overlay the cell's rows show. Omit for non-scenario surfaces.
   */
  scenarioKey?: string;
  /**
   * ISO stat-holiday dates — the continuous staffing windows are holiday-aware
   * (setup anchors past holidays; a Saturday end tears down Monday; an
   * all-holiday end week pushes teardown into the next week). Omit →
   * weekend-only windows.
   */
  holidays?: string[];
}

export interface WeeklyCellModel {
  /** The test isn't scheduled this week and nobody is assigned. */
  isEmpty: boolean;
  /** One entry per machine booking overlapping the week. */
  machineGroups: WeeklyCellMachineGroup[];
  /** Legacy task-level (null-booking) rows shown only when ≥2 machines exist. */
  taskLevelAssignees: TaskAssignee[];
  /** All rows for this week (the task-level "+" picker's current assignees). */
  weekAssignments: TaskAssignee[];
  /** Which group's "+" picker is open (booking key | TASK_LEVEL_PICKER | null). */
  pickerFor: string | null;
  setPickerFor: (id: string | null) => void;
  // Per-row accessors (use the live removing / baseline state)
  isRemoving: (a: TaskAssignee) => boolean;
  /** 6C.6 — this user is also staffed on this task/week in the BASELINE plan. */
  showBaseline: (a: TaskAssignee) => boolean;
  // Handlers
  removeAssignee: (key: string) => void;
  onBookingResized: () => void;
  onPickerAssigned: () => void;
  onPickerUnassigned: () => void;
  // Remove confirmation (shared by all views) — × asks before deleting.
  /**
   * Open the "remove employee?" confirm for one assignee. `userKey` feeds the
   * advisory stranding check (W2-8): when removing this row would leave a
   * junior (below L3) alone in THIS week, the confirm's message says so and
   * names the week + juniors — proceed is always allowed (warn-only).
   */
  requestRemove: (key: string, name: string, userKey: string) => void;
  /** Dismiss the confirm without removing. */
  cancelRemove: () => void;
  /** Ready-to-render ConfirmDialog state; render <ConfirmDialog state={…} onClose={cancelRemove}/>. */
  removeConfirmState: ConfirmDialogState;
  /**
   * P-17a: a failed remove DELETE is no longer silent — this holds the message
   * for the red snackbar both presentations render. null = no error.
   */
  saveError: string | null;
  /** Dismiss the save-error snackbar. */
  clearSaveError: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useWeeklyCellModel({
  task,
  weekStart,
  assignments,
  onAssignmentsChanged,
  onAllocationChanged,
  baselineAssignments,
  compareToBaseline,
  scenarioKey,
  holidays,
}: UseWeeklyCellModelArgs): WeeklyCellModel {
  // Lab answer Q2 (2026-07-10): continuous tasks staff the FULL working-day
  // duration — the windows below cover every week of [setup..teardown].
  const taskContinuous = !task.IsPrepStep && isContinuousRun(task.RunMode ?? null);
  // ── Derive bookings that overlap this week ─────────────────────────────────
  const machineSegments: MachineSegment[] = task.Bookings
    .map((b) => {
      const seg = bookingSegmentInWeek(b.StartDate, b.EndDate, weekStart);
      if (!seg) return null;
      return {
        key: b.MachineBookingKey,
        code: b.MachineCode ?? b.MachineKey,
        name: b.MachineName,
        bookingStart: b.StartDate,
        bookingEnd: b.EndDate,
        status: b.Status,
        ...seg,
      };
    })
    .filter((s): s is MachineSegment => s !== null);

  // ── Derive assignments for this week ──────────────────────────────────────
  const weekAssignments = assignments.filter((a) => a.WeekStart === weekStart);

  // Per-machine staffing (079): a single machine this week folds legacy task-level
  // (null-booking) rows into that one group; with ≥2 machines they stay separate.
  const isSoleMachine = machineSegments.length === 1;

  // ── Group week assignees by machine booking ───────────────────────────────
  const baseGroups = machineSegments.map((seg) => {
    const matchWeek = weekAssignments.filter(
      (a) => belongsToBooking(a, seg.key) || (isSoleMachine && !a.MachineBookingKey),
    );
    const matchAll = assignments.filter(
      (a) => belongsToBooking(a, seg.key) || (isSoleMachine && !a.MachineBookingKey),
    );
    return { seg, weekAssignees: matchWeek, anchorKeys: dedupeAnchorKeys(matchAll) };
  });

  // Catch-all bucket: week-rows not shown in any machine group (legacy task-level
  // rows with ≥2 machines, plus any orphan whose booking has no segment this week).
  const renderedInMachineGroups = new Set<string>();
  for (const g of baseGroups) {
    for (const a of g.weekAssignees) renderedInMachineGroups.add(a.EmployeeAssignmentKey);
  }
  const taskLevelAssignees = weekAssignments.filter(
    (a) => !renderedInMachineGroups.has(a.EmployeeAssignmentKey),
  );

  // 6C.6 baseline ghost — PRESENCE only (Wave 3): the set of users staffed on
  // this task/week in the BASELINE plan. The stored baseline percents are
  // ignored (Q4A — readers key on presence, not the number).
  const baselineWeekUsers = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    if (!compareToBaseline || !baselineAssignments) return s;
    for (const a of baselineAssignments) {
      if (a.WeekStart === weekStart) s.add(a.UserKey);
    }
    return s;
  }, [compareToBaseline, baselineAssignments, weekStart]);

  // ── State ─────────────────────────────────────────────────────────────────
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(new Set());
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  // `strand` starts undefined (check in flight); the advisory result upgrades the
  // open confirm's message in place when it lands. null = check failed/none —
  // keep the generic copy (never block, never a second popup). W2-8 (P-15).
  const [pendingRemove, setPendingRemove] = useState<{
    key: string;
    name: string;
    strand?: RemovalStrandResult | null;
  } | null>(null);
  // P-17a: failed remove writes surface here (red snackbar) instead of silently
  // reverting — apiClient only banners 503s.
  const [saveError, setSaveError] = useState<string | null>(null);
  const clearSaveError = useCallback(() => { setSaveError(null); }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRemove = useCallback(
    async (key: string) => {
      setRemovingKeys((prev) => new Set(prev).add(key));
      try {
        await deleteEmployeeAssignment(key);
        onAssignmentsChanged(task.ScheduleTaskKey);
        onAllocationChanged?.();
      } catch {
        // P-17a: a failed DELETE used to be an UNHANDLED REJECTION (try/finally
        // with no catch) — surface it instead; the row stays and the grid is honest.
        setSaveError('Could not remove — try again.');
      } finally {
        setRemovingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [task.ScheduleTaskKey, onAssignmentsChanged, onAllocationChanged],
  );
  const removeAssignee = useCallback((key: string) => { void handleRemove(key); }, [handleRemove]);
  const requestRemove = useCallback((key: string, name: string, userKey: string) => {
    // Open the confirm immediately with the generic copy, then run the advisory
    // stranding check (W2-8): week-scoped to THIS cell and excluding the row
    // being removed (a user still present on another machine this week can't
    // strand). Best-effort — a failed check keeps the generic message.
    setPendingRemove({ key, name });
    void checkRemovalStranding(task.ScheduleTaskKey, userKey, scenarioKey, {
      weekStart,
      excludeAssignmentKey: key,
    })
      .then((strand) => {
        setPendingRemove((prev) => (prev && prev.key === key ? { ...prev, strand } : prev));
      })
      .catch(() => {
        setPendingRemove((prev) => (prev && prev.key === key ? { ...prev, strand: null } : prev));
      });
  }, [task.ScheduleTaskKey, scenarioKey, weekStart]);
  const cancelRemove = useCallback(() => { setPendingRemove(null); }, []);

  const onPickerAssigned = useCallback(() => {
    setPickerFor(null);
    onAssignmentsChanged(task.ScheduleTaskKey);
    onAllocationChanged?.();
  }, [task.ScheduleTaskKey, onAssignmentsChanged, onAllocationChanged]);

  const onPickerUnassigned = useCallback(() => {
    onAssignmentsChanged(task.ScheduleTaskKey);
    onAllocationChanged?.();
  }, [task.ScheduleTaskKey, onAssignmentsChanged, onAllocationChanged]);

  const onBookingResized = useCallback(() => {
    onAssignmentsChanged(task.ScheduleTaskKey);
    onAllocationChanged?.();
  }, [task.ScheduleTaskKey, onAssignmentsChanged, onAllocationChanged]);

  // "Needs attention" is simply "nobody assigned to this machine this week".
  // Lab answer Q2 (2026-07-10): the full-duration windows cover every week of
  // [setup..teardown], so ordinary mid-run weeks of a continuous booking are
  // now normal staffed weeks (amber nag + Add CTA restored — RM-6's exemption
  // retired). The flag only remains for a week with NO staffable working day
  // (rare all-holiday lead-in), which the backend would still 400.
  const machineGroups: WeeklyCellMachineGroup[] = baseGroups.map((g) => {
    const midRunContinuous =
      taskContinuous &&
      !continuousAttendanceWindows(g.seg.bookingStart, g.seg.bookingEnd, holidays)
        .some((w) => w.weekStart === weekStart);
    return {
      ...g,
      midRunContinuous,
      under: g.weekAssignees.length === 0 && !midRunContinuous,
    };
  });

  const isEmpty =
    machineSegments.length === 0 && weekAssignments.length === 0 && pickerFor === null;

  // Ready-to-render confirm state. onConfirm captures the key from THIS render (the
  // shared ConfirmDialog calls onClose before onConfirm, so it must not read state).
  // W2-8 (P-15): when the advisory stranding check reports this row's user is the
  // last L3+ of the week, the SAME dialog carries the warning (v0.909 prior-art
  // copy, week-scoped) — proceed stays allowed, warn-only.
  const strands = Boolean(
    pendingRemove?.strand?.strands && (pendingRemove.strand.strandedJuniors.length ?? 0) > 0,
  );
  const removeConfirmState: ConfirmDialogState = pendingRemove
    ? strands
      ? {
          open: true,
          title: 'Remove the last supervisor this week?',
          message:
            `${pendingRemove.name} is the only L3+ supervisor on the week of ` +
            `${fmtShortDate(weekStart)} for this task. Removing them leaves ` +
            `${(pendingRemove.strand as RemovalStrandResult).strandedJuniors
              .map((j) => `${j.displayName} (L${j.level ?? '?'})`)
              .join(', ')} unsupervised. Remove anyway?`,
          confirmLabel: 'Remove anyway',
          confirmColor: 'warning',
          onConfirm: () => removeAssignee(pendingRemove.key),
        }
      : {
          open: true,
          title: 'Remove employee?',
          message: `Remove ${pendingRemove.name} from this week?`,
          confirmLabel: 'Remove',
          confirmColor: 'error',
          onConfirm: () => removeAssignee(pendingRemove.key),
        }
    : CONFIRM_INITIAL;

  return {
    isEmpty,
    machineGroups,
    taskLevelAssignees,
    weekAssignments,
    pickerFor,
    setPickerFor,
    isRemoving: (a) => removingKeys.has(a.EmployeeAssignmentKey),
    showBaseline: (a) => Boolean(compareToBaseline) && baselineWeekUsers.has(a.UserKey),
    removeAssignee,
    onBookingResized,
    onPickerAssigned,
    onPickerUnassigned,
    requestRemove,
    cancelRemove,
    removeConfirmState,
    saveError,
    clearSaveError,
  };
}
